<template>
	<div class="app-container" :class="bodyClass">

		<div class="screen-pre-tutorial" :class="{ active: !screenPreTutorialLoaded }"></div>

		<MaoHeader v-if="loaded" />
		<MaoFirstLanguage v-if="!loaded && !selectedLanguage" @onSelectLang="(lang) => selectedLanguage = lang" />
		<MaoBottomMenu />
		<MaoFullDataLoader @onLoaded="dataLoaded" :selectedLanguage="selectedLanguage" />
		<router-view v-slot="{ Component, route }">

			<transition name="fade" mode="out-in">
				<component :is="Component" :key="route.path" v-if="Component && loaded" />
			</transition>

		</router-view>
		<div class="skip-transition"></div>
		<MaoZoom />
		<MaoLoader />
		<transition name="fade" mode="out-in">
			<MaoFooter v-if="loaded" />
		</transition>
		<MaoCookie />
	</div>
</template>

<script>
import MaoHeader from "@/components/MaoHeader.vue";
import MaoFullDataLoader from "@/components/MaoFullDataLoader.vue";
import MaoBottomMenu from "@/components/MaoBottomMenu.vue";
import MaoZoom from "@/components/MaoZoom.vue";
import MaoAudioPlayer from "@/components/MaoAudioPlayer.vue";
import MaoLoader from "@/components/MaoLoader.vue";
import MaoFooter from "@/components/MaoFooter.vue";
import MaoFirstLanguage from "@/components/MaoFirstLanguage.vue";
import MaoCookie from "./components/MaoCookie.vue";
import { RouterView } from "vue-router";
import { useGlobalActionsStore } from "@/stores/globalActions";
import { useAppDataStore } from "@/stores/appData";
import { defineComponent, watch } from "vue";

export default defineComponent({
	name: "App",
	components: {
		RouterView,
		MaoHeader,
		MaoFullDataLoader,
		MaoBottomMenu,
		MaoZoom,
		MaoAudioPlayer,
		MaoLoader,
		MaoFooter,
		MaoFirstLanguage,
		MaoCookie
	},
	setup() {
		const globalActionsStore = useGlobalActionsStore();
		const appDataStore = useAppDataStore();

		watch(
			() => globalActionsStore.preventScrolling,
			newVal => {
				if (newVal) {
					document.body.classList.add("prevent-scrolling");
				} else {
					document.body.classList.remove("prevent-scrolling");
				}
			},
			{ immediate: true }
		);

		return { globalActionsStore, appDataStore };
	},
	methods: {
		dataLoaded() {
			this.loaded = true;
			setTimeout(() => {
				this.screenPreTutorialLoaded = true;
			}, 1000);
		},
	},
	computed: {
		preventScrolling() {
			return this.globalActionsStore.preventScrolling;
		}
	},
	watch: {
		$route() {

			const tutorialCompleted = localStorage.getItem("tutorialCompleted");

			if (!tutorialCompleted && this.$route.name != 'Tutorial') {
				let currentLang = this.selectedLanguage;
				if (this.$route.params.lang) {
					currentLang = this.$route.params.lang;
				}
				else if (window.location.pathname.split('/')[1]) {
					currentLang = window.location.pathname.split('/')[1];
				}

				if (!this.globalActionsStore.redirect) {
					
					let redirect = {
						redirectName: this.$route.name,
						lang: currentLang,
					};


					if(this.$route.name == 'Artwork' || this.$route.name == 'Path' || this.$route.name == 'PathArtwork') {
						redirect.redirectId = this.$route.params.id;
						if(this.$route.params.artworkId) {
							redirect.artworkId = this.$route.params.artworkId;
						}
					}

					this.globalActionsStore.setRedirect(redirect);

					this.$router.push({
						name: 'Tutorial', params: {
							lang: currentLang,
						}
					});
				}

			}
			else {
				if (localStorage.getItem("firstLanguageLang")) {
					this.$router.push({ name: this.$route.name, params: { lang: localStorage.getItem("firstLanguageLang") } });
				}
			}

			if (this.$route.name == "PathArtwork") {
				this.bodyClass = "dark-background";
			} else {
				this.bodyClass = "";
			}

			// based on lang add to bodyclass lang-xx
			if (this.$route.params.lang) {
				document.body.classList.forEach((className) => {
					if (className.startsWith("lang-")) {
						document.body.classList.remove(className);
					}
				});
				document.body.classList.add("lang-" + this.$route.params.lang);
			}
		},
		"appDataStore.appData.loaded": {
			handler: function (newVal) {
				if (newVal) {
					setTimeout(() => {
						this.screenPreTutorialLoaded = true;
					}, 1000);
				}
				else {
					this.screenPreTutorialLoaded = false;
				}

			},
			immediate: true
		},
	},
	mounted() {
		const link = document.createElement("link");
		link.href = process.env.VUE_APP_FIGMA_CSS_URL + "?v=figma-css-" + Math.random().toString(36).substring(7);
		link.type = "text/css";
		link.rel = "stylesheet";
		document.head.appendChild(link);

		// on press tab key add class is-user-tabbing to body
		document.body.addEventListener("keydown", (event) => {
			if (event.key === "Tab") {
				document.body.classList.add("is-user-tabbing");
			}
		});


	},
	data() {
		return {
			loaded: false,
			bodyClass: "",
			screenPreTutorialLoaded: false,
			selectedLanguage: localStorage.getItem("firstLanguageLang")
		};
	}
});
</script>

<style>
html,
body {
	height: 100%;
	overflow-y: scroll;
}


body {
	position: fixed;
	width: 100%;
	overflow-y: scroll;
}

.is-user-tabbing *:focus {
	outline: 3px solid #2f00ff;
}

.app-container {
	min-height: 100vh;
}

.prevent-scrolling {
	overflow: hidden;
}

body {
	&::-webkit-scrollbar {
		width: 0;
	}
}

main {
	min-height: calc(100vh - 5.3rem);
}

.lang-ar {
	direction: rtl;
}
</style>
<style lang="scss" scoped>
.dark-background {
	background-color: var(--surface-background-variant-dark);
}
</style>
